import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Form, Input, InputNumber } from 'antd';
import moment, { Moment } from 'moment';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { DropdownOption } from '@app/models/DropdownOption';
import { getProducts } from '@app/api/pre-clearance.api';
import { notificationController } from '@app/controllers/notificationController';

const DATE_FORMAT = 'YYYY-MM-DD';
const NOT_SELECTED = 'Not Selected';

type FieldType = 'text' | 'textarea' | 'decimal' | 'date' | 'select' | 'multiselect';

interface FieldConfig {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: DropdownOption[];
  precision?: number;
  loading?: boolean;
  disabled?: boolean;
  onChange?: (value: unknown) => void;
}

type PreClearanceInfoProps = {
  editable: boolean;
  status: string | null;
  acceptedFields?: Record<string, boolean>;
  productCategories: DropdownOption[];
  countries: DropdownOption[];
  locationOptions: DropdownOption[];
};

/* ---------- helpers ---------- */

const findOptionLabel = (options: DropdownOption[] = [], value: unknown): string | undefined => {
  const option = options.find((o) => o.value === value);
  return option ? String(option.label) : undefined;
};

// Form stores dates as 'YYYY-MM-DD' strings; DatePicker needs a Moment.
const dateItemProps = {
  getValueProps: (value: string) => ({ value: value ? moment(value) : null }),
  normalize: (date: Moment | null) => (date ? date.format(DATE_FORMAT) : ''),
};

const getPlaceholder = (field: FieldConfig) =>
  ['select', 'multiselect', 'date'].includes(field.type) ? `Select ${field.label}` : `Enter ${field.label}`;

const getReadOnlyFormatter = (field: FieldConfig): ((value: unknown) => string) | undefined => {
  switch (field.type) {
    case 'select':
      return (value) => findOptionLabel(field.options, value) ?? NOT_SELECTED;
    case 'multiselect':
      return (value) => {
        const labels = Array.isArray(value) ? value.map((v) => findOptionLabel(field.options, v)).filter(Boolean) : [];
        return labels.length ? labels.join(', ') : NOT_SELECTED;
      };
    default:
      return undefined;
  }
};

/* ---------- field renderers ---------- */

interface ReadOnlyFieldProps {
  value?: unknown; // injected by Form.Item
  format?: (value: unknown) => string;
  multiline?: boolean;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ value, format, multiline }) => {
  const text = format ? format(value) : value == null ? '' : String(value);
  return multiline ? (
    <Input.TextArea readOnly autoSize bordered={false} value={text} />
  ) : (
    <BaseInput readOnly value={text} />
  );
};

const renderEditableInput = (field: FieldConfig, disabled: boolean): React.ReactNode => {
  const placeholder = getPlaceholder(field);

  switch (field.type) {
    case 'textarea':
      return <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} placeholder={placeholder} disabled={disabled} />;
    case 'decimal':
      return (
        <InputNumber
          style={{ width: '100%' }}
          min={0}
          precision={field.precision}
          placeholder={placeholder}
          disabled={disabled}
        />
      );
    case 'date':
      return (
        <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} placeholder={placeholder} disabled={disabled} />
      );
    case 'select':
    case 'multiselect':
      return (
        <BaseSelect
          mode={field.type === 'multiselect' ? 'multiple' : undefined}
          allowClear
          showSearch
          optionFilterProp="label"
          options={field.options}
          placeholder={placeholder}
          loading={field.loading}
          disabled={disabled || field.disabled}
          onChange={field.onChange}
          style={{ width: '100%' }}
        />
      );
    default:
      return <Input placeholder={placeholder} disabled={disabled} />;
  }
};

/* ---------- component ---------- */

const PreClearanceInfo: React.FC<PreClearanceInfoProps> = ({
  editable,
  status,
  acceptedFields = {},
  productCategories,
  countries,
  locationOptions,
}) => {
  const userType = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}')?.usertype, []);
  const isEditable = editable && userType === 'CUSTOMER' && (status === 'DRAFT' || status === 'New');

  const form = Form.useFormInstance();
  const categoryId = Form.useWatch('product_category_id', form);
  const [productOptions, setProductOptions] = useState<DropdownOption[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<DropdownOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // Load products & departments for the selected category
  useEffect(() => {
    if (!categoryId) {
      setProductOptions([]);
      setDepartmentOptions([]);
      return;
    }

    let cancelled = false; // ignore stale responses when the category changes quickly
    setOptionsLoading(true);

    getProducts(categoryId)
      .then(({ product_options, department_options }) => {
        if (cancelled) return;
        setProductOptions(product_options);
        setDepartmentOptions(department_options);

        // Auto-fill department only when there's exactly one choice and nothing is selected yet
        if (isEditable && department_options.length === 1 && !form.getFieldValue('department_id')) {
          form.setFieldsValue({ department_id: department_options[0].value });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setProductOptions([]);
        setDepartmentOptions([]);
        notificationController.error({ message: err.message });
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryId, isEditable, form]);

  const fields = useMemo<FieldConfig[]>(
    () => [
      {
        name: 'product_category_id',
        label: 'Product Category',
        type: 'select',
        required: true,
        options: productCategories,
        onChange: () => form.setFieldsValue({ product_id: undefined, department_id: undefined }),
      },
      {
        name: 'product_id',
        label: 'Product',
        type: 'select',
        required: true,
        options: productOptions,
        loading: optionsLoading,
        disabled: !categoryId,
      },
      { name: 'country_ids', label: 'Countries', type: 'multiselect', options: countries },
      { name: 'location_id', label: 'Port of Entry', type: 'select', required: true, options: locationOptions },
      {
        name: 'department_id',
        label: 'Department',
        type: 'select',
        required: true,
        options: departmentOptions,
        loading: optionsLoading,
        disabled: !categoryId,
      },
      { name: 'invoice_no', label: 'Performa Invoice No', type: 'text', required: true },
      { name: 'invoice_date', label: 'Performa Invoice Date', type: 'date', required: true },
      { name: 'quantity', label: 'Quantity', type: 'decimal', required: true },
      { name: 'hs_code', label: 'H.S. Code', type: 'text' },
      { name: 'value_usd', label: 'Value (US$)', type: 'decimal', precision: 2 },
      { name: 'supplier_name', label: 'Supplier Name', type: 'text' },
      { name: 'supplier_reg', label: 'Supplier Registration No', type: 'text' },
      { name: 'supplier_address', label: 'Supplier Address', type: 'textarea' },
      { name: 'importer_name', label: 'Importer Name', type: 'text', required: true },
      { name: 'importer_address', label: 'Importer Address', type: 'textarea', required: true },
      { name: 'pc_business_name', label: 'Business Name', type: 'text' },
      { name: 'pc_business_address', label: 'Business Address', type: 'textarea' },
    ],
    [
      productCategories,
      countries,
      locationOptions,
      departmentOptions,
      productOptions,
      optionsLoading,
      categoryId,
      form,
    ],
  );

  return (
    <BaseRow>
      {fields.map((field) => {
        const rules = isEditable && field.required ? [{ required: true, message: `${field.label} is required` }] : [];
        const extraItemProps = isEditable && field.type === 'date' ? dateItemProps : {};

        return (
          <BaseCol key={field.name} xs={24} md={24} lg={12}>
            <Form.Item name={field.name} label={field.label} rules={rules} {...extraItemProps}>
              {isEditable ? (
                renderEditableInput(field, !!acceptedFields[field.name])
              ) : (
                <ReadOnlyField format={getReadOnlyFormatter(field)} multiline={field.type === 'textarea'} />
              )}
            </Form.Item>
          </BaseCol>
        );
      })}
    </BaseRow>
  );
};

export default PreClearanceInfo;
